// src/lib/format.ts
const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });

/**
 * Human-readable project period from ISO dates, e.g.
 * "Jun – Aug 2024", "Nov 2023 – Feb 2024", "Since Jan 2025".
 */
export function formatPeriod(start?: string | null, end?: string | null): string {
    if (!start) return '';
    const s = new Date(start);
    if (!end) return `Since ${month(s)} ${s.getUTCFullYear()}`;
    const e = new Date(end);
    if (s.getUTCFullYear() === e.getUTCFullYear()) {
        return s.getUTCMonth() === e.getUTCMonth()
            ? `${month(s)} ${s.getUTCFullYear()}`
            : `${month(s)} – ${month(e)} ${e.getUTCFullYear()}`;
    }
    return `${month(s)} ${s.getUTCFullYear()} – ${month(e)} ${e.getUTCFullYear()}`;
}

export function formatDate(iso: string, style: 'long' | 'short' = 'long'): string {
    return new Date(iso).toLocaleDateString('en-US', { month: style, day: 'numeric', year: 'numeric' });
}

/** Drop a leading "# Title" from Markdown when it repeats the page title. */
export function stripLeadingTitle(md: string, title: string): string {
    const text = md.trim();
    const m = text.match(/^#\s+(.+)\n?/);
    return m && m[1].trim().toLowerCase() === title.trim().toLowerCase()
        ? text.slice(m[0].length).trim()
        : text;
}

/** Plain-text excerpt of Markdown content, without a leading heading. */
export function postPreview(md: string, length = 220): string {
    const text = (md || '')
        .replace(/^\s*#{1,6}[^\n]*(\n|\\n)/, '')
        .replace(/\\n/g, ' ')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/<[^>]*>/g, '')
        .replace(/[#*_>`\-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return text.length > length ? text.slice(0, length).replace(/\s+\S*$/, '') + '…' : text;
}

/** Estimated reading time at ~200 words per minute, at least one minute. */
export function readingTime(md: string): string {
    const words = (md || '').replace(/```[\s\S]*?```/g, ' ').split(/\s+/).filter(Boolean).length;
    return `${Math.max(1, Math.round(words / 200))} min read`;
}
